import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { CreditCard, AlertTriangle, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Dashboard widget: shows credit-card health snapshot per credit account.
 *
 * For each credit account it fetches /api/accounts/{id}/credit-status and
 * displays the most actionable signals (days to payment + interest-free amount)
 * with a semaphore. Sorted by urgency (red > amber > green) and days_to_payment asc.
 */
const TONE = {
  green: { text: "text-[#10B981]", bg: "bg-[#10B981]/15", border: "border-[#10B981]/30", Icon: CheckCircle2, label: "Sano", order: 2 },
  amber: { text: "text-[#F59E0B]", bg: "bg-[#F59E0B]/15", border: "border-[#F59E0B]/30", Icon: Clock, label: "Atento", order: 1 },
  red: { text: "text-[#EF4444]", bg: "bg-[#EF4444]/15", border: "border-[#EF4444]/30", Icon: AlertTriangle, label: "Urgente", order: 0 },
};

function classifyTone(util, daysPay) {
  if (daysPay < 3 || util >= 80) return "red";
  if (daysPay <= 7 || util >= 50) return "amber";
  return "green";
}

const fmtShortDate = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
};

export default function CreditCardsHealthCard({ currency = "MXN" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const { data: accounts } = await api.get("/accounts");
        const credits = (accounts || []).filter((a) => a.type === "credit");
        if (!credits.length) {
          if (active) {
            setItems([]);
            setLoading(false);
          }
          return;
        }
        const statuses = await Promise.all(
          credits.map(async (acc) => {
            try {
              const { data } = await api.get(`/accounts/${acc.id}/credit-status`);
              return { account: acc, status: data };
            } catch {
              return null;
            }
          })
        );
        const valid = statuses.filter((s) => s && s.status && s.status.configured);
        valid.sort((a, b) => {
          const ta = classifyTone(a.status.utilization_pct ?? 0, a.status.days_to_payment ?? 999);
          const tb = classifyTone(b.status.utilization_pct ?? 0, b.status.days_to_payment ?? 999);
          if (TONE[ta].order !== TONE[tb].order) return TONE[ta].order - TONE[tb].order;
          return (a.status.days_to_payment ?? 999) - (b.status.days_to_payment ?? 999);
        });
        if (active) {
          setItems(valid);
          setLoading(false);
        }
      } catch {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="clay-card p-5" data-testid="credit-cards-health-loading">
        <div className="h-4 w-32 bg-[#1B2638] rounded animate-pulse mb-3" />
        <div className="h-12 bg-[#1B2638] rounded animate-pulse" />
      </div>
    );
  }

  if (!items.length) return null; // No credit cards configured -> hide widget

  return (
    <div className="clay-card p-5" data-testid="credit-cards-health-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#0052FF]/15 flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-[#0052FF]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] font-medium">
              Tarjetas de crédito
            </p>
            <h3 className="font-display text-lg font-semibold text-[#F1F5F9]">
              Salud y pagos
            </h3>
          </div>
        </div>
        <Link
          to="/cuentas"
          className="text-[11px] text-[#0052FF] hover:underline flex items-center gap-0.5"
          data-testid="credit-cards-health-link-accounts"
        >
          Ver cuentas <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      <ul className="space-y-3" data-testid="credit-cards-health-list">
        {items.map(({ account, status }) => {
          const util = status.utilization_pct ?? 0;
          const daysPay = status.days_to_payment ?? 999;
          const tone = classifyTone(util, daysPay);
          const t = TONE[tone];
          const ToneIcon = t.Icon;
          return (
            <li
              key={account.id}
              className={`p-3 rounded-xl border ${t.border} ${t.bg} flex items-center gap-3`}
              data-testid={`credit-card-health-row-${account.id}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.text} bg-[#0F172A]`}>
                <ToneIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F1F5F9] truncate" data-testid={`credit-card-health-name-${account.id}`}>
                  {account.name}
                </p>
                <p className="text-[11px] text-[#94A3B8]">
                  Pagar{" "}
                  <span className="font-numeric text-[#F1F5F9]" data-testid={`credit-card-health-amount-${account.id}`}>
                    {formatCurrency(status.interest_free_amount ?? 0, currency)}
                  </span>
                  {status.next_payment_date && (
                    <> · vence {fmtShortDate(status.next_payment_date)}</>
                  )}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-numeric font-semibold ${t.text}`} data-testid={`credit-card-health-days-${account.id}`}>
                  {daysPay < 0
                    ? `${Math.abs(daysPay)} d. atrasado`
                    : daysPay === 0
                    ? "Hoy"
                    : `${daysPay} d.`}
                </p>
                <p className="text-[10px] text-[#64748B]">uso {util}%</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
